/**
 * @fileoverview Payments client for interacting with the payments service.
 * Provides type-safe methods for customer management, subscriptions,
 * payments, refunds, and checkout sessions.
 * @module @eoln/sdk-payments
 */

import { ErrorAuthentication, ErrorAuthorization } from "@eoln/errors";
import { type RetryConfig, RetryManager } from "@eoln/resilience";
import type {
  BillingAddressInput,
  BillingPortalSession,
  CheckoutInput,
  CheckoutSession,
  CreateCustomerInput,
  Customer,
  CustomerListResponse,
  InvoiceReference,
  PaginationInput,
  Payment,
  PaymentListResponse,
  PaymentStatus,
  Refund,
  RefundInput,
  RefundListResponse,
  Subscription,
  SubscriptionListResponse,
  TaxIdInput,
} from "../../../srv/payments/__generated__/types.ts";

/**
 * GraphQL error structure returned by the server
 */
interface GraphQLError {
  message: string;
  extensions?: {
    code?: string;
    requiredScopes?: string[];
  };
}

/**
 * Generic GraphQL response wrapper
 */
interface GraphQLResponse<T = unknown> {
  data?: T;
  errors?: GraphQLError[];
}

// ============================================================================
// GraphQL Response Types
// ============================================================================

interface GetCustomerResponse {
  customer: Customer | null;
}

interface ListCustomersResponse {
  customers: CustomerListResponse;
}

interface CreateCustomerResponse {
  createCustomer: Customer;
}

interface DeleteCustomerResponse {
  deleteCustomer: boolean;
}

interface GetSubscriptionResponse {
  subscription: Subscription | null;
}

interface ListSubscriptionsResponse {
  subscriptions: SubscriptionListResponse;
}

interface CancelSubscriptionResponse {
  cancelSubscription: Subscription;
}

interface GetPaymentResponse {
  payment: Payment | null;
}

interface ListPaymentsResponse {
  payments: PaymentListResponse;
}

interface GetRefundResponse {
  refund: Refund | null;
}

interface ListRefundsResponse {
  refunds: RefundListResponse;
}

interface CreateRefundResponse {
  createRefund: Refund;
}

interface CreateCheckoutSessionResponse {
  createCheckoutSession: CheckoutSession;
}

interface CreateBillingPortalSessionResponse {
  createBillingPortalSession: BillingPortalSession;
}

// ============================================================================
// GraphQL Fragments
// ============================================================================

const CUSTOMER_FIELDS = `
  customerId
  name
  email
  status
  tier
  stripeCustomerId
  taxIdentity {
    countryCode
    number
    legalName
    addressLine1
    addressLine2
    addressCountry
    source
    verifiedAt
  }
  createdAt
  updatedAt
`;

const CUSTOMER_WITH_SUBSCRIPTIONS_FIELDS = `
  ${CUSTOMER_FIELDS}
  subscriptions {
    subscriptionId
    status
    currentPeriodEnd
    cancelAtPeriodEnd
  }
`;

const SUBSCRIPTION_FIELDS = `
  subscriptionId
  customerId
  provider
  status
  currentPeriodStart
  currentPeriodEnd
  cancelAtPeriodEnd
  createdAt
  updatedAt
`;

const PAYMENT_FIELDS = `
  paymentId
  customerId
  subscriptionId
  provider
  amountCents
  currency
  status
  paymentMethod
  createdAt
  paidAt
`;

const REFUND_FIELDS = `
  refundId
  paymentId
  provider
  amountCents
  currency
  status
  reason
  createdAt
  refundedAt
`;

// ============================================================================
// Client Configuration
// ============================================================================

/**
 * Default retry configuration for the PaymentsClient.
 * Retries up to 3 times with exponential backoff starting at 100ms.
 */
const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxAttempts: 3,
  initialDelayMs: 100,
  maxDelayMs: 2000,
  backoffMultiplier: 2,
  jitter: true,
  name: "PaymentsClient",
};

/**
 * Configuration options for the PaymentsClient.
 */
export interface PaymentsClientOptions {
  /**
   * URL of the GraphQL API endpoint for the payments service.
   * Default: http://localhost:8003/graphql
   */
  endpoint?: string;

  /**
   * API key for authentication.
   */
  apiKey?: string;

  /**
   * Retry configuration for transient failures.
   * Set to `false` to disable retries entirely.
   * Default: 3 attempts with exponential backoff (100ms, 200ms, 400ms).
   */
  retry?: Partial<RetryConfig> | false;
}

// ============================================================================
// PaymentsClient Class
// ============================================================================

/**
 * Client for interacting with the payments service.
 *
 * Provides methods for:
 * - Customer management (create, get, list)
 * - Subscription management (get, list, cancel)
 * - Payment queries (get, list)
 * - Refund management (create, get, list)
 * - Checkout sessions (create checkout, create billing portal)
 */
export class PaymentsClient {
  private endpoint: string;
  private apiKey: string | undefined;
  private retryManager: RetryManager | null;

  /**
   * Creates a new PaymentsClient instance.
   *
   * @param options - Configuration options
   */
  constructor(options: PaymentsClientOptions = {}) {
    this.endpoint = options.endpoint || "http://localhost:8003/graphql";
    this.apiKey = options.apiKey;

    // Initialize retry manager (null if disabled)
    if (options.retry === false) {
      this.retryManager = null;
    } else {
      const retryConfig: RetryConfig = {
        ...DEFAULT_RETRY_CONFIG,
        ...options.retry,
      };
      this.retryManager = new RetryManager(retryConfig);
    }
  }

  /**
   * Sets the API key for this client instance.
   *
   * @param apiKey - The API key to use for authentication
   */
  setApiKey(apiKey: string): void {
    this.apiKey = apiKey;
  }

  /**
   * Gets the currently configured API key for this client instance.
   *
   * @returns The current API key or undefined if not set
   */
  getConfiguredApiKey(): string | undefined {
    return this.apiKey;
  }

  /**
   * Gets health metrics for monitoring retry behavior.
   * Required for all SDK clients using resilience.
   *
   * @returns Object containing retry metrics
   */
  getHealthMetrics(): {
    retry: {
      totalAttempts: number;
      totalSuccesses: number;
      totalFailures: number;
      lastAttemptTime: number | null;
    };
  } {
    return {
      retry: this.retryManager?.getMetrics() ?? {
        totalAttempts: 0,
        totalSuccesses: 0,
        totalFailures: 0,
        lastAttemptTime: null,
      },
    };
  }

  // ==========================================================================
  // Customer Methods
  // ==========================================================================

  /**
   * Creates a new customer.
   *
   * @param input - Customer creation input
   * @returns The created customer
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing payments:write scope
   */
  async createCustomer(input: CreateCustomerInput): Promise<Customer> {
    const query = `
      mutation CreateCustomer($input: CreateCustomerInput!) {
        createCustomer(input: $input) {
          ${CUSTOMER_WITH_SUBSCRIPTIONS_FIELDS}
        }
      }
    `;

    const response = (await this.graphqlRequest(query, {
      input,
    })) as CreateCustomerResponse;
    return response.createCustomer;
  }

  /**
   * Completes an invoice the pipeline parked for a missing buyer tax identity
   * (#172): verifies `taxId` when given and stores it on the customer, then
   * reserves the number and submits to KSeF. Requires `payments:admin`.
   *
   * @param invoiceId - The parked invoice's id (invoice_references.invoice_id)
   * @param taxId - The buyer's Polish NIP; omit when the customer is already verified
   * @param billingAddress - Only when the registry gives the buyer no address
   */
  async completeParkedInvoice(
    invoiceId: string,
    taxId?: TaxIdInput,
    billingAddress?: BillingAddressInput,
  ): Promise<InvoiceReference> {
    const query = `
      mutation CompleteParkedInvoice($invoiceId: ID!, $taxId: TaxIdInput, $billingAddress: BillingAddressInput) {
        completeParkedInvoice(invoiceId: $invoiceId, taxId: $taxId, billingAddress: $billingAddress) {
          invoiceId invoiceNumber checkoutSessionId customerId status ksefInstanceId totalGrossGrosze createdAt updatedAt
        }
      }
    `;
    const response = (await this.graphqlRequest(query, { invoiceId, taxId, billingAddress })) as {
      completeParkedInvoice: InvoiceReference;
    };
    return response.completeParkedInvoice;
  }

  /**
   * Gets a customer by ID.
   *
   * @param customerId - The customer's UUID
   * @returns The customer or null if not found
   * @throws {ErrorAuthentication} If not authenticated
   */
  async getCustomer(customerId: string): Promise<Customer | null> {
    const query = `
      query GetCustomer($customerId: UUID!) {
        customer(customerId: $customerId) {
          ${CUSTOMER_WITH_SUBSCRIPTIONS_FIELDS}
        }
      }
    `;

    const response = (await this.graphqlRequest(query, {
      customerId,
    })) as GetCustomerResponse;
    return response.customer;
  }

  /**
   * Deletes a customer.
   *
   * @param customerId - The customer's UUID
   * @returns True if deleted
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing payments:write scope
   */
  async deleteCustomer(customerId: string): Promise<boolean> {
    const query = `
      mutation DeleteCustomer($customerId: UUID!) {
        deleteCustomer(customerId: $customerId)
      }
    `;

    const response = (await this.graphqlRequest(query, {
      customerId,
    })) as DeleteCustomerResponse;
    return response.deleteCustomer;
  }

  /**
   * Lists customers with pagination and optional search.
   *
   * @param pagination - Pagination options (default: limit 50, offset 0)
   * @param search - Optional case-insensitive match on email or name
   * @returns Paginated customer list response
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing payments:read scope
   */
  async listCustomers(pagination: PaginationInput = {}, search?: string): Promise<CustomerListResponse> {
    const query = `
      query ListCustomers($pagination: PaginationInput!, $search: String) {
        customers(pagination: $pagination, search: $search) {
          customers {
            ${CUSTOMER_FIELDS}
          }
          total
          hasMore
        }
      }
    `;

    const response = (await this.graphqlRequest(query, {
      pagination: { limit: 50, offset: 0, ...pagination },
      search: search ?? null,
    })) as ListCustomersResponse;
    return response.customers;
  }

  // ==========================================================================
  // Subscription Methods
  // ==========================================================================

  /**
   * Gets a subscription by ID.
   *
   * @param subscriptionId - The subscription's UUID
   * @returns The subscription or null if not found
   * @throws {ErrorAuthentication} If not authenticated
   */
  async getSubscription(subscriptionId: string): Promise<Subscription | null> {
    const query = `
      query GetSubscription($subscriptionId: UUID!) {
        subscription(subscriptionId: $subscriptionId) {
          ${SUBSCRIPTION_FIELDS}
        }
      }
    `;

    const response = (await this.graphqlRequest(query, {
      subscriptionId,
    })) as GetSubscriptionResponse;
    return response.subscription;
  }

  /**
   * Lists subscriptions with optional customer filter.
   *
   * @param customerId - Optional customer ID to filter by
   * @param pagination - Pagination options (default: limit 50, offset 0)
   * @returns Paginated subscription list response
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing payments:read scope
   */
  async listSubscriptions(customerId?: string, pagination: PaginationInput = {}): Promise<SubscriptionListResponse> {
    const query = `
      query ListSubscriptions($customerId: UUID, $pagination: PaginationInput!) {
        subscriptions(customerId: $customerId, pagination: $pagination) {
          subscriptions {
            ${SUBSCRIPTION_FIELDS}
          }
          total
          hasMore
        }
      }
    `;

    const response = (await this.graphqlRequest(query, {
      customerId,
      pagination: { limit: 50, offset: 0, ...pagination },
    })) as ListSubscriptionsResponse;
    return response.subscriptions;
  }

  /**
   * Cancels a subscription.
   *
   * @param subscriptionId - The subscription's UUID
   * @param cancelAtPeriodEnd - If true, subscription remains active until period ends (default: true)
   * @returns The updated subscription
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing payments:write scope
   */
  async cancelSubscription(subscriptionId: string, cancelAtPeriodEnd = true): Promise<Subscription> {
    const query = `
      mutation CancelSubscription($subscriptionId: UUID!, $cancelAtPeriodEnd: Boolean) {
        cancelSubscription(subscriptionId: $subscriptionId, cancelAtPeriodEnd: $cancelAtPeriodEnd) {
          ${SUBSCRIPTION_FIELDS}
        }
      }
    `;

    const response = (await this.graphqlRequest(query, {
      subscriptionId,
      cancelAtPeriodEnd,
    })) as CancelSubscriptionResponse;
    return response.cancelSubscription;
  }

  // ==========================================================================
  // Payment Methods
  // ==========================================================================

  /**
   * Gets a payment by ID.
   *
   * @param paymentId - The payment's UUID
   * @returns The payment or null if not found
   * @throws {ErrorAuthentication} If not authenticated
   */
  async getPayment(paymentId: string): Promise<Payment | null> {
    const query = `
      query GetPayment($paymentId: UUID!) {
        payment(paymentId: $paymentId) {
          ${PAYMENT_FIELDS}
        }
      }
    `;

    const response = (await this.graphqlRequest(query, {
      paymentId,
    })) as GetPaymentResponse;
    return response.payment;
  }

  /**
   * Lists payments with optional filters.
   *
   * @param customerId - Optional customer ID to filter by
   * @param status - Optional payment status to filter by
   * @param pagination - Pagination options (default: limit 50, offset 0)
   * @returns Paginated payment list response
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing payments:read scope
   */
  async listPayments(
    customerId?: string,
    status?: PaymentStatus,
    pagination: PaginationInput = {},
  ): Promise<PaymentListResponse> {
    const query = `
      query ListPayments($customerId: UUID, $status: PaymentStatus, $pagination: PaginationInput!) {
        payments(customerId: $customerId, status: $status, pagination: $pagination) {
          payments {
            ${PAYMENT_FIELDS}
          }
          total
          hasMore
        }
      }
    `;

    const response = (await this.graphqlRequest(query, {
      customerId,
      status,
      pagination: { limit: 50, offset: 0, ...pagination },
    })) as ListPaymentsResponse;
    return response.payments;
  }

  // ==========================================================================
  // Refund Methods
  // ==========================================================================

  /**
   * Creates a refund for a payment.
   *
   * @param input - Refund input (paymentId required, amountCents optional for partial)
   * @returns The created refund
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing payments:refund scope
   */
  async createRefund(input: RefundInput): Promise<Refund> {
    const query = `
      mutation CreateRefund($input: RefundInput!) {
        createRefund(input: $input) {
          ${REFUND_FIELDS}
        }
      }
    `;

    const response = (await this.graphqlRequest(query, {
      input,
    })) as CreateRefundResponse;
    return response.createRefund;
  }

  /**
   * Gets a refund by ID.
   *
   * @param refundId - The refund's UUID
   * @returns The refund or null if not found
   * @throws {ErrorAuthentication} If not authenticated
   */
  async getRefund(refundId: string): Promise<Refund | null> {
    const query = `
      query GetRefund($refundId: UUID!) {
        refund(refundId: $refundId) {
          ${REFUND_FIELDS}
        }
      }
    `;

    const response = (await this.graphqlRequest(query, {
      refundId,
    })) as GetRefundResponse;
    return response.refund;
  }

  /**
   * Lists refunds with optional payment filter.
   *
   * @param paymentId - Optional payment ID to filter by
   * @param pagination - Pagination options (default: limit 50, offset 0)
   * @returns Paginated refund list response
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing payments:read scope
   */
  async listRefunds(paymentId?: string, pagination: PaginationInput = {}): Promise<RefundListResponse> {
    const query = `
      query ListRefunds($paymentId: UUID, $pagination: PaginationInput!) {
        refunds(paymentId: $paymentId, pagination: $pagination) {
          refunds {
            ${REFUND_FIELDS}
          }
          total
          hasMore
        }
      }
    `;

    const response = (await this.graphqlRequest(query, {
      paymentId,
      pagination: { limit: 50, offset: 0, ...pagination },
    })) as ListRefundsResponse;
    return response.refunds;
  }

  // ==========================================================================
  // Checkout Methods
  // ==========================================================================

  /**
   * Creates a Stripe checkout session.
   *
   * @param input - Checkout input with customerId, priceId, and URLs
   * @returns The checkout session with URL to redirect customer
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing payments:write scope
   */
  async createCheckoutSession(input: CheckoutInput): Promise<CheckoutSession> {
    const query = `
      mutation CreateCheckoutSession($input: CheckoutInput!) {
        createCheckoutSession(input: $input) {
          sessionId
          url
          expiresAt
          provider
        }
      }
    `;

    const response = (await this.graphqlRequest(query, {
      input,
    })) as CreateCheckoutSessionResponse;
    return response.createCheckoutSession;
  }

  /**
   * Creates a Stripe billing portal session.
   *
   * @param customerId - The customer's UUID
   * @returns The billing portal session with URL
   * @throws {ErrorAuthentication} If not authenticated
   */
  async createBillingPortalSession(customerId: string): Promise<BillingPortalSession> {
    const query = `
      mutation CreateBillingPortalSession($customerId: UUID!) {
        createBillingPortalSession(customerId: $customerId) {
          id
          url
        }
      }
    `;

    const response = (await this.graphqlRequest(query, {
      customerId,
    })) as CreateBillingPortalSessionResponse;
    return response.createBillingPortalSession;
  }

  // ==========================================================================
  // Private Methods
  // ==========================================================================

  /**
   * Makes a GraphQL request to the payments service.
   * Automatically retries on transient failures (network errors, 5xx, 429).
   *
   * @param query - The GraphQL query or mutation
   * @param variables - Optional variables for the query
   * @returns The GraphQL response data
   * @throws {ErrorAuthentication} If authentication fails (not retried)
   * @throws {ErrorAuthorization} If authorization fails (not retried)
   * @throws {RetryExhaustedError} If all retry attempts fail
   * @throws {Error} If the request fails or returns GraphQL errors
   */
  private async graphqlRequest<T = unknown>(query: string, variables?: Record<string, unknown>): Promise<T> {
    const executeRequest = async (): Promise<T> => {
      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };

      if (this.apiKey) {
        headers.Authorization = `Bearer ${this.apiKey}`;
        headers["X-API-Key"] = this.apiKey;
      }

      const response = await fetch(this.endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify({
          query,
          variables,
        }),
      });

      // Check for HTTP-level errors that should trigger retry
      if (response.status >= 500 || response.status === 429) {
        const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
        (error as Error & { status: number }).status = response.status;
        throw error;
      }

      const result = (await response.json()) as GraphQLResponse<T>;

      if (result.errors) {
        const error = result.errors[0];

        // Auth errors should NOT be retried
        if (error.extensions?.code === "UNAUTHENTICATED") {
          throw new ErrorAuthentication(error.message);
        }

        if (error.extensions?.code === "FORBIDDEN") {
          throw new ErrorAuthorization(error.message, error.extensions?.requiredScopes || ["payments:read"]);
        }

        throw new Error(error.message);
      }

      if (!result.data) {
        throw new Error("GraphQL response missing data field");
      }

      return result.data;
    };

    // Execute with or without retry
    if (this.retryManager) {
      return this.retryManager.execute(executeRequest, this.isRetryableError);
    }

    return executeRequest();
  }

  /**
   * Determines if an error should trigger a retry.
   * Retries: network errors, timeouts, 5xx server errors, 429 rate limiting.
   * Does NOT retry: authentication/authorization errors, validation errors.
   */
  private isRetryableError = (error: unknown): boolean => {
    // Never retry auth errors
    if (error instanceof ErrorAuthentication || error instanceof ErrorAuthorization) {
      return false;
    }

    if (error instanceof Error) {
      const message = error.message.toLowerCase();

      // Network and connectivity errors
      if (
        message.includes("network") ||
        message.includes("timeout") ||
        message.includes("econnrefused") ||
        message.includes("enotfound") ||
        message.includes("econnreset") ||
        message.includes("etimedout") ||
        message.includes("socket hang up") ||
        message.includes("fetch failed")
      ) {
        return true;
      }

      // HTTP 5xx errors
      if ("status" in error) {
        const status = (error as { status: unknown }).status;
        if (typeof status === "number" && status >= 500 && status < 600) {
          return true;
        }
        // Rate limiting
        if (status === 429) {
          return true;
        }
      }
    }

    return false;
  };
}
